import {useLayoutEffect, useRef} from "react";
import {createRequestRefreshScheduler} from "./requestRefreshScheduler";

const useRequestRefreshScheduler = (request, {
    delay = 1000,
    onError,
    applyResult,
    debounce = true,
    deferWhenHidden = false,
    refreshKey,
} = {}) => {
    const requestRef = useRef(request);
    const onErrorRef = useRef(onError);
    const applyResultRef = useRef(applyResult);
    const deferWhenHiddenRef = useRef(deferWhenHidden);
    const hiddenRefreshPendingRef = useRef(false);
    requestRef.current = request;
    onErrorRef.current = onError;
    applyResultRef.current = applyResult;
    deferWhenHiddenRef.current = deferWhenHidden;

    const shouldDefer = () => deferWhenHiddenRef.current && document.hidden;
    const deferredResult = () => ({deferred: true, verified: false});

    const schedulerRef = useRef();
    const ensureScheduler = () => {
        schedulerRef.current ||= createRequestRefreshScheduler({
            request: () => {
                if(shouldDefer()) {
                    hiddenRefreshPendingRef.current = true;
                    return deferredResult();
                }
                return requestRef.current();
            },
            applyResult: result => {
                if(!result?.deferred) applyResultRef.current?.(result);
            },
            onError: error => onErrorRef.current?.(error),
            delay,
            debounce,
        });
        return schedulerRef.current;
    };

    const facadeRef = useRef();
    facadeRef.current ||= {
        schedule: callback => {
            if(shouldDefer()) {
                hiddenRefreshPendingRef.current = true;
                callback?.({ok: true, result: deferredResult()});
                return;
            }
            ensureScheduler().schedule(callback);
        },
        schedulePromise: () => {
            if(shouldDefer()) {
                hiddenRefreshPendingRef.current = true;
                return Promise.resolve(deferredResult());
            }
            return ensureScheduler().schedulePromise();
        },
    };

    // Ownership cleanup must run during commit so an old in-flight result cannot
    // apply between a route change and passive-effect cleanup.
    useLayoutEffect(() => {
        hiddenRefreshPendingRef.current = false;
        const scheduler = ensureScheduler();
        const refreshWhenVisible = () => {
            if(document.hidden || !hiddenRefreshPendingRef.current) return;

            hiddenRefreshPendingRef.current = false;
            scheduler.schedule();
        };
        if(deferWhenHidden) document.addEventListener('visibilitychange', refreshWhenVisible);

        return () => {
            if(deferWhenHidden) document.removeEventListener('visibilitychange', refreshWhenVisible);
            hiddenRefreshPendingRef.current = false;
            scheduler.dispose();
            if(schedulerRef.current === scheduler) schedulerRef.current = undefined;
        };
    }, [deferWhenHidden, refreshKey]);

    return facadeRef.current;
};

export default useRequestRefreshScheduler;
